import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Dialog, Typography } from '@mui/material'
import LinkOffIcon from '@mui/icons-material/LinkOffRounded'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CreditCardOffIcon from '@mui/icons-material/CreditCardOffOutlined'
import { colors } from '../../const/colors'
import SimpleCheckbox from '../Shared/SimpleCheckbox'
import { SectionActions, SectionButton } from '../Shared/SectionButton'

const DetachUserModal: FC<{
  open: boolean
  userName: string
  hasCard: boolean
  hasActiveReservations: boolean
  withCard: boolean
  setWithCard: (value: boolean) => void
  onClose: () => void
  onConfirm: () => void
}> = ({
  open,
  userName,
  hasCard,
  hasActiveReservations,
  withCard,
  setWithCard,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(['parking_pool', 'btn'])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: colors.radiusModal,
          width: 460,
          maxWidth: '100%',
          p: '26px',
          m: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            background: colors.redBg,
            color: colors.red,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <LinkOffIcon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '19px',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: colors.navy,
            }}
          >
            {t('parking_pool:unassign_modal_title')}
          </Typography>
          <Typography
            sx={{ fontSize: '13px', color: colors.textMuted, fontWeight: 600 }}
          >
            {userName}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: '13.5px',
          color: colors.textSecondary,
          lineHeight: 1.55,
          m: '18px 0 0',
        }}
      >
        {t('parking_pool:unassign_modal_lead')}
      </Typography>

      {hasActiveReservations && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '9px',
            mt: '14px',
            p: '12px 14px',
            borderRadius: '12px',
            background: colors.redBg,
            border: `1px solid ${colors.redBorder}`,
            color: colors.red,
            fontSize: '12.5px',
            fontWeight: 600,
            lineHeight: 1.45,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 17, flexShrink: 0 }} />
          {t('parking_pool:unassign_active_reservation')}
        </Box>
      )}

      {hasCard && (
        <Box
          sx={{
            mt: '16px',
            p: '10px 14px 14px',
            borderRadius: '12px',
            background: colors.bg,
            border: `1px solid ${colors.border}`,
          }}
        >
          <SimpleCheckbox
            checked={withCard}
            setChecked={setWithCard}
            label={t('parking_pool:unassign_with_card')}
            e2e="detach-with-card"
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              mt: '2px',
              pl: '2px',
              fontSize: '12.5px',
              lineHeight: 1.45,
              fontWeight: 600,
              color: withCard ? colors.red : colors.textMuted,
            }}
          >
            <CreditCardOffIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            {t('parking_pool:unassign_card_warning')}
          </Box>
        </Box>
      )}

      <SectionActions>
        <SectionButton
          label={t('btn:cancel')}
          tone="neutral"
          onClick={onClose}
        />
        <SectionButton
          label={t('parking_pool:unassign_user_short')}
          icon={<LinkOffIcon sx={{ fontSize: 17 }} />}
          tone="danger"
          e2e="detach-confirm"
          onClick={onConfirm}
        />
      </SectionActions>
    </Dialog>
  )
}

export default DetachUserModal
